/**
 * useCook – thin wrapper around Nuxt 3 `useCookie`.
 *
 * **Automatic sane defaults**
 *   • `watch`: `'shallow'` – reactive but cheap
 *   • `maxAge`: 6 months
 *   • `path`: `'/'`
 *   • `sameSite`: `'lax'` – CSRF‑safe by default
 *   • `secure`: `true` when running under HTTPS or in production build
 *   • `httpOnly`: `true` when executed on server side (hides cookie from XSS)
 *
 * Includes optional runtime console‑trace in dev mode for quick audit.
 */

import { useCookie } from '#app/composables/cookie'
import { useRuntimeConfig } from '#app'

type UseCookieOptions = Parameters<typeof useCookie>[1]

export function useCook<T = string>(
  name: string,
  opts: UseCookieOptions = {}
) {
  if (opts.watch === undefined) {
    opts = { ...opts, watch: 'shallow' }
  }
  if (opts.maxAge === undefined) {
    opts = { ...opts, maxAge: 60 * 60 * 24 * 31 * 6 }
  }
  if (opts.path === undefined) {
    opts = { ...opts, path: '/' }
  }

  // Security‑related sane defaults
  if (opts.sameSite === undefined) {
    opts = { ...opts, sameSite: 'lax' }
  }

  if (opts.secure === undefined) {
    const isHttps =
      process.server
        ? useRuntimeConfig().app.baseURL?.startsWith('https')
        : typeof window !== 'undefined' && window.location.protocol === 'https:'
    opts = { ...opts, secure: !!isHttps }
  }

  // Only the server can set HttpOnly
  if (process.server && opts.httpOnly === undefined) {
    opts = { ...opts, httpOnly: true }
  }

  // DEV‑only debug: log every call once per component render
  if (process.dev && (globalThis as any).__DEBUG_COOKIES__ !== false) {
    // console.log(
    //   `[useCook] ${name} | watch: ${opts.watch}`,
    //   new Error().stack?.split('\n').slice(2, 6).join(' → ')
    // )
  }

  return useCookie<T>(name, opts)
}

/**
 * Helper to toggle debug at runtime:
 *   import { toggleCookDebug } from '~/composables/useCook'
 *   toggleCookDebug(false) // disable
 */
export function toggleCookDebug(on = true) {
  ;(globalThis as any).__DEBUG_COOKIES__ = on
}

export default useCook